import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Film, ImageIcon, Play, X } from 'lucide-react';
import { useMediaItemUrl, cachedMediaUrl } from './mediaUrl';
import { DurationChip, ItemBackdrop, ItemImage } from './MediaTile';
import { PostMediaItem, extractYouTubeId, hasDuration } from './mediaUtils';

const GAP = 10;
const SCROLL_LOCK_MS = 500;

function videoLabel(item: PostMediaItem): string {
  if (item.source === 'youtube') return 'YouTube video';
  if (item.source === 'vidcast') return 'Vidcast';
  return 'Uploaded video';
}

function slideStep(track: HTMLElement | null): number {
  const slide = track?.firstElementChild as HTMLElement | null;
  return slide ? slide.getBoundingClientRect().width + GAP : 0;
}

function scrollToIndex(track: HTMLElement | null, i: number, smooth: boolean) {
  if (track && typeof track.scrollTo === 'function') {
    track.scrollTo({ left: i * slideStep(track), behavior: smooth ? 'smooth' : 'auto' });
  }
}

function Slide({ postId, item, title, current, near }: {
  postId: string
  item: PostMediaItem
  title: string
  current: boolean
  near: boolean
}) {
  const [active, setActive] = useState(false);
  const isFileVideo = item.kind === 'video' && item.source === 'upload';
  const { url, error } = useMediaItemUrl(postId, item.id, current && isFileVideo);

  useEffect(() => {
    if (!current) setActive(false);
  }, [current]);

  if (item.kind === 'image') {
    return <ItemImage postId={postId} item={item} alt={title} enabled={near} className="h-full w-full object-contain" />;
  }

  if (isFileVideo && current) {
    if (url) return <video src={url} controls className="h-full w-full bg-black" />;
    return <div className="flex h-full w-full items-center justify-center text-xs text-white/50">{error ? 'Media unavailable' : 'Loading...'}</div>;
  }

  if (current && active) {
    const ytId = item.source === 'youtube' ? extractYouTubeId(item.videoUrl) : null;
    if (item.source === 'youtube' && ytId) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          title={title}
        />
      );
    }
    if (item.source === 'vidcast') {
      return <iframe src={item.videoUrl} className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={title} />;
    }
  }

  const playable = current && !isFileVideo;
  return (
    <button
      type="button"
      aria-label={playable ? 'Play video' : undefined}
      tabIndex={playable ? 0 : -1}
      disabled={!playable}
      onClick={() => setActive(true)}
      className="relative block h-full w-full bg-black"
    >
      <ItemBackdrop item={item} />
      <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#dc2626]/90 shadow-xl">
        <Play className="ml-0.5 h-6 w-6 text-white" fill="white" />
      </span>
      <DurationChip duration={item.duration} className="bottom-2 right-2" />
    </button>
  );
}

function StripThumb({ postId, item }: { postId: string; item: PostMediaItem }) {
  if (item.kind === 'image') {
    const url = cachedMediaUrl(postId, item.id);
    if (url) return <img src={url} alt="" draggable={false} className="h-full w-full object-cover" />;
    return <span className="flex h-full w-full items-center justify-center bg-[#1a2332]"><ImageIcon className="h-4 w-4 text-white/50" /></span>;
  }
  const hasThumb = item.source === 'youtube' && extractYouTubeId(item.videoUrl);
  return (
    <>
      <ItemBackdrop item={item} />
      {!hasThumb && <Film className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-white/60" />}
      <DurationChip duration={item.duration} className="bottom-0.5 right-0.5 !text-[10px]" />
    </>
  );
}

export default function MediaViewer({ postId, items, title, startIndex, onClose }: {
  postId: string
  items: PostMediaItem[]
  title: string
  startIndex: number
  onClose: () => void
}) {
  const count = items.length;
  const [index, setIndex] = useState(Math.min(startIndex, count - 1));
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const lockUntil = useRef(0);

  const goTo = useCallback((i: number) => {
    const next = Math.max(0, Math.min(count - 1, i));
    lockUntil.current = Date.now() + SCROLL_LOCK_MS;
    setIndex(next);
    scrollToIndex(trackRef.current, next, true);
  }, [count]);

  useLayoutEffect(() => {
    scrollToIndex(trackRef.current, index, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inVideo = (e.target as HTMLElement)?.tagName === 'VIDEO';
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && !inVideo) goTo(index - 1);
      else if (e.key === 'ArrowRight' && !inVideo) goTo(index + 1);
      else if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex="0"]'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, goTo, onClose]);

  const onScroll = () => {
    if (Date.now() < lockUntil.current) return;
    const track = trackRef.current;
    const w = slideStep(track);
    if (!w || !track) return;
    const i = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / w)));
    if (i !== index) setIndex(i);
  };

  const item = items[index];
  const isVideo = item.kind === 'video';
  const caption = isVideo ? videoLabel(item) : `Photo ${index + 1}`;
  const sub = isVideo ? (hasDuration(item.duration) ? item.duration : '') : title;
  const nav = 'absolute top-1/2 z-[2] -translate-y-1/2 rounded-full bg-[#080c18]/55 p-2 text-white transition hover:bg-[#080c18]/80 disabled:hidden';

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      className="fixed inset-0 z-[120] flex flex-col bg-[#050810]/95 pb-4 pt-3.5 text-white"
    >
      <div className="flex items-center justify-between px-4 pb-2.5">
        <span className="font-semibold tabular-nums" data-testid="viewer-counter">{index + 1} / {count}</span>
        <button ref={closeRef} type="button" aria-label="Close viewer" onClick={onClose} className="rounded-full bg-white/15 p-2 text-white hover:bg-white/25">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center">
        <div className="relative mx-auto w-full max-w-[980px]">
          <div
            ref={trackRef}
            onScroll={onScroll}
            className="flex snap-x snap-mandatory overflow-x-auto px-[8%] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{ gap: GAP, scrollPaddingInline: '8%' }}
          >
            {items.map((it, i) => (
              <div
                key={`${it.id}:${i}`}
                className="relative aspect-video shrink-0 basis-[84%] snap-center overflow-hidden rounded-xl bg-[#050b18]"
                data-testid="viewer-slide"
              >
                <Slide postId={postId} item={it} title={title} current={i === index} near={Math.abs(i - index) <= 1} />
              </div>
            ))}
          </div>
          <button type="button" aria-label="Previous media" disabled={index === 0} onClick={() => goTo(index - 1)} className={`${nav} left-2`}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" aria-label="Next media" disabled={index === count - 1} onClick={() => goTo(index + 1)} className={`${nav} right-2`}>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-0.5 px-4 pb-1.5 pt-2.5 text-center">
        <strong className="font-semibold" data-testid="viewer-caption">{caption}</strong>
        {sub && <span className="text-[13px] tabular-nums text-white/70">{sub}</span>}
      </div>

      <div className="flex gap-2 overflow-x-auto px-4 pt-2 [justify-content:safe_center]">
        {items.map((it, i) => (
          <button
            key={`${it.id}:${i}`}
            type="button"
            aria-label={`Item ${i + 1}`}
            aria-current={i === index ? 'true' : undefined}
            onClick={() => goTo(i)}
            className={`relative aspect-video w-[74px] shrink-0 overflow-hidden rounded-md border-2 bg-[#050b18] p-0 transition ${i === index ? 'border-white opacity-100' : 'border-transparent opacity-60 hover:opacity-90'}`}
          >
            <StripThumb postId={postId} item={it} />
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
